// Frozen enums for the pedigree analyzer. Values are stable identifiers;
// user-visible labels are resolved via i18next from the `*LabelKey` fields on
// results, never hardcoded here.
//
// Carrier status is REUSED from the shipped carrier-status module (do not
// redefine) — see PLAN-pedigree-analysis-clinical-insights.md §12.2.
import { CARRIER_STATUS, isCarrierMark } from '../carrier-status/constants';

export { CARRIER_STATUS, isCarrierMark };

// Raw pedigree gender → karyotype-relevant sex token.
export const SEX = Object.freeze({ M: 'M', F: 'F', U: 'U' });
export function toSex(gender) {
  if (gender === 'male') return SEX.M;
  if (gender === 'female') return SEX.F;
  return SEX.U;
}

export const CONFIDENCE = Object.freeze({
  strong: 'strong',
  moderate: 'moderate',
  weak: 'weak',
  ruledOut: 'ruled_out',
  none: 'none'
});

// Mendelian / non-Mendelian inheritance patterns.
export const PATTERN = Object.freeze({
  AD: 'pattern.autosomal_dominant',
  AR: 'pattern.autosomal_recessive',
  XLD: 'pattern.x_linked_dominant',
  XLR: 'pattern.x_linked_recessive',
  MT: 'pattern.mitochondrial',
  YL: 'pattern.y_linked'
});

// TGCLOUD-1669 — display labels for the two enums below, in ONE place.
//
// There were three copies of this: PedigreeMatchMenu's PATTERN_LABEL,
// cascadePriors' MODE_LABEL (whose own comment said "that copy is presentation
// side and should ultimately import from here"), and very nearly a third in the
// collapsed-section summary, which is how the summary came to render the raw id
// `pattern.autosomal_recessive` on screen.
//
// Stored mid-sentence, because that is the harder form to derive: capitalising
// the first character of 'autosomal recessive' gives a correct label, while
// lower-casing 'X-linked dominant' does not.
export const PATTERN_MODE_LABEL = Object.freeze({
  'pattern.autosomal_dominant': 'autosomal dominant',
  'pattern.autosomal_recessive': 'autosomal recessive',
  'pattern.x_linked_dominant': 'X-linked dominant',
  'pattern.x_linked_recessive': 'X-linked recessive',
  'pattern.mitochondrial': 'mitochondrial',
  'pattern.y_linked': 'Y-linked'
});

/**
 * Sentence-case label for an inheritance pattern id.
 * Falls back to the id, so an unmapped pattern is visible rather than blank —
 * but every id in PATTERN is mapped, and a test pins that.
 */
export const patternLabel = (id) => {
  const mode = PATTERN_MODE_LABEL[id];
  if (!mode) return id;
  return mode.charAt(0).toUpperCase() + mode.slice(1);
};

export const ROLE_LABEL = Object.freeze({
  proband: 'Proband',
  affected: 'Affected',
  obligate_carrier: 'Obligate carrier',
  tested_carrier: 'Tested carrier',
  reported_carrier: 'Reported carrier',
  carrier: 'Carrier',
  cascade_candidate: 'Cascade candidate',
  unaffected_at_risk_relative: 'Unaffected at-risk relative',
  unaffected: 'Unaffected',
  unknown: 'Unknown'
});

export const ROLE = Object.freeze({
  proband: 'proband',
  affected: 'affected',
  obligateCarrier: 'obligate_carrier',
  testedCarrier: 'tested_carrier',
  reportedCarrier: 'reported_carrier',
  // Carrier with no stated basis (provenance unspecified).
  carrier: 'carrier',
  cascadeCandidate: 'cascade_candidate',
  unaffectedAtRisk: 'unaffected_at_risk_relative',
  unaffected: 'unaffected',
  unknown: 'unknown'
});

export const ALERT_KIND = Object.freeze({
  variantConflict: 'variant_conflict',
  dataGap: 'data_gap',
  reducedPenetrance: 'reduced_penetrance_flag',
  consanguinity: 'consanguinity'
});

export const RECOMMENDED_ACTION = Object.freeze({
  targetedTest: 'targeted_test',
  panelTest: 'panel_test',
  counsellingFirst: 'counselling_first',
  defer: 'defer'
});
